import React, { useState, useEffect } from 'react';
import { Upload, Eye, Edit2, Trash2, Image, Film, File, X } from 'lucide-react';
import { mediaAPI, uploadAPI } from '../../../../services/dashboardAPI';

const MediaManagement = ({ showNotification }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const types = ['all', 'image', 'video', 'document'];
  const categories = ['all', 'homepage', 'events', 'gallery', 'testimonials'];

  const [formData, setFormData] = useState({
    name: '',
    type: 'image',
    category: 'homepage',
    description: '',
    fileUrl: '',
  });

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterType !== 'all') filters.type = filterType;
      if (filterCategory !== 'all') filters.category = filterCategory;
      
      const response = await mediaAPI.getAll(filters);
      if (response.success) {
        setMedia(response.data);
      } else {
        showNotification('Failed to fetch media', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
    }
  };

  const uploadMedia = async () => {
    if (!mediaFile) return null;
    
    try {
      setUploading(true);
      const folderMap = {
        image: 'media/images',
        video: 'media/videos',
        document: 'media/documents',
      };
      const folder = folderMap[formData.type] || 'media';
      
      const response = await uploadAPI.uploadFile(mediaFile, folder);
      if (response.success) {
        return response.data.url;
      } else {
        showNotification('Upload failed', 'error');
        return null;
      }
    } catch (error) {
      showNotification(error.message, 'error');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSaveMedia = async () => {
    if (!formData.name || !formData.type) {
      showNotification('Name and type are required', 'error');
      return;
    }

    try {
      setLoading(true);
      let fileUrl = formData.fileUrl;
      
      if (mediaFile) {
        fileUrl = await uploadMedia();
        if (!fileUrl) {
          setLoading(false);
          return;
        }
      }

      const mediaPayload = {
        ...formData,
        fileUrl,
      };

      let response;
      if (editingId) {
        response = await mediaAPI.update(editingId, mediaPayload);
      } else {
        response = await mediaAPI.create(mediaPayload);
      }

      if (response.success) {
        showNotification(editingId ? 'Media updated!' : 'Media added!');
        setShowForm(false);
        setEditingId(null);
        setFormData({
          name: '',
          type: 'image',
          category: 'homepage',
          description: '',
          fileUrl: '',
        });
        setMediaFile(null);
        fetchMedia();
      } else {
        showNotification('Failed to save media', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
    setMediaFile(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        setLoading(true);
        const response = await mediaAPI.delete(id);
        if (response.success) {
          showNotification('Media deleted!');
          fetchMedia();
        } else {
          showNotification('Failed to delete', 'error');
        }
      } catch (error) {
        showNotification(error.message, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      type: 'image',
      category: 'homepage',
      description: '',
      fileUrl: '',
    });
    setMediaFile(null);
  };

  const filtered = media.filter(m => 
    (filterType === 'all' || m.type === filterType) &&
    (filterCategory === 'all' || m.category === filterCategory)
  );

  return (
    <>
      <div className="section-header">
        <div>
          <h2 className="title">Media Management</h2>
          <p className="subtitle">Manage images, videos, and documents</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
          disabled={loading}
        >
          <Upload size={18} /> Upload Media
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="form-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingId ? 'Edit Media' : 'Add Media'}</h3>
            <button onClick={handleCloseForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>

          <div className="form-group">
            <label>Name *</label>
            <input 
              type="text"
              placeholder="Enter media name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Type *</label>
            <select 
              value={formData.type}
              onChange={(e) => setFormData({...formData, type: e.target.value})}
            >
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
            </select>
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select 
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
            >
              <option value="homepage">Homepage</option>
              <option value="events">Events</option>
              <option value="gallery">Gallery</option>
              <option value="testimonials">Testimonials</option>
            </select>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea 
              placeholder="Enter description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Upload File *</label>
            <input 
              type="file"
              onChange={handleFileSelect}
            />
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={handleCloseForm} disabled={loading}>
              Cancel
            </button>
            <button 
              className="btn-primary" 
              onClick={handleSaveMedia}
              disabled={loading || uploading}
            >
              {loading || uploading ? 'Saving...' : editingId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-group">
          <label>Type:</label>
          {types.map(t => (
            <button
              key={t}
              className={`filter-btn ${filterType === t ? 'active' : ''}`}
              onClick={() => { setFilterType(t); }}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="filter-group">
          <label>Category:</label>
          {categories.map(c => (
            <button
              key={c}
              className={`filter-btn ${filterCategory === c ? 'active' : ''}`}
              onClick={() => { setFilterCategory(c); }}
            >
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Media List */}
      {loading && !showForm ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No media found</div>
      ) : (
        <div className="media-list" style={{ marginTop: '20px' }}>
          {filtered.map(item => (
            <div key={item.id} className="media-item" style={{ display: 'flex', alignItems: 'center', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', marginBottom: '10px' }}>
              <div className="media-icon" style={{ marginRight: '15px', minWidth: '40px' }}>
                {item.type === 'image' && <Image size={32} />}
                {item.type === 'video' && <Film size={32} />}
                {item.type === 'document' && <File size={32} />}
              </div>
              <div className="media-info" style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 5px 0' }}>{item.name}</h4>
                <div className="media-meta" style={{ display: 'flex', gap: '10px', fontSize: '12px', color: '#888' }}>
                  <span className="badge">{item.type}</span>
                  <span>{item.category}</span>
                  {item.fileSize && <span>{(item.fileSize / 1024 / 1024).toFixed(2)} MB</span>}
                </div>
              </div>
              <div className="media-actions" style={{ display: 'flex', gap: '10px' }}>
                <button className="icon-btn" onClick={() => handleEdit(item)} disabled={loading} title="Edit">
                  <Edit2 size={16} />
                </button>
                <button className="icon-btn" onClick={() => handleDelete(item.id)} disabled={loading} title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default MediaManagement;
